import React from 'react';
import { Link } from 'react-router-dom';
import { getMediaListType, genreTagListLink, searchListLink, splitCommaField } from '../../utils/mediaLinks';

const MediaInfo = ({ mediaItem, editedMedia, editMode, isAdmin, handleInputChange }) => {
  const type = mediaItem.type.toLowerCase();
  const isAnimeWithSeasons = (type.includes('anime') || type.includes('animemovie') || type.includes('animeserie')) && mediaItem.seasons?.length > 0;
  const season1 = isAnimeWithSeasons ? mediaItem.seasons[0] : null;
  const totalPlaytime = mediaItem.playtime;
  const hasMultiplePlatforms = [mediaItem.playtime_windows, mediaItem.playtime_deck, mediaItem.playtime_linux].filter(Boolean).length > 1;
  const mediaListType = getMediaListType(mediaItem);

  // Renders a comma-separated people/company field (e.g. "MAPPA, Studio Trigger") as separate
  // links into the media-list's search box, one per name.
  const renderSearchableValue = (value) =>
    splitCommaField(value).map((part, index, arr) => (
      <React.Fragment key={part}>
        <Link to={searchListLink(mediaListType, part)} className="info-value-link">{part}</Link>
        {index < arr.length - 1 ? ', ' : ''}
      </React.Fragment>
    ));

  return (
    <div className="info-card">
      <h2>Details</h2>
      {/* Titles for Anime (compact, Season 1 if multiple seasons) */}
      {(type.includes('anime') || type.includes('manga') || type.includes('animemovie') || type.includes('animeserie')) && (
        <div className="info-section">
          <h3>
            <img src="/static/icons/language-solid.svg" alt="Titles" className="section-icon icon" />
            Titles
          </h3>
          <div className="info-details compact-titles">
            {isAnimeWithSeasons ? (
              <>
                {season1.title_en && (
                  <div className="info-item">
                    <span className="info-label">English:</span>
                    <span className="info-value">{season1.title_en}</span>
                  </div>
                )}
                {season1.title_mal && (
                  <div className="info-item">
                    <span className="info-label">MAL:</span>
                    <span className="info-value">{season1.title_mal}</span>
                  </div>
                )}
                {season1.title_ja && (
                  <div className="info-item">
                    <span className="info-label">Japanese:</span>
                    <span className="info-value">{season1.title_ja}</span>
                  </div>
                )}
              </>
            ) : (
              <>
                {mediaItem.title_en && (
                  <div className="info-item">
                    <span className="info-label">English:</span>
                    <span className="info-value">{mediaItem.title_en}</span>
                  </div>
                )}
                {mediaItem.title_mal && (
                  <div className="info-item">
                    <span className="info-label">MAL:</span>
                    <span className="info-value">{mediaItem.title_mal}</span>
                  </div>
                )}
                {mediaItem.title_ja && (
                  <div className="info-item">
                    <span className="info-label">Japanese:</span>
                    <span className="info-value">{mediaItem.title_ja}</span>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Description full-width */}
      <div className="info-section full-width">
        <h3>
          <img src="/static/icons/align-left-solid.svg" alt="Description" className="section-icon icon" />
          Description
        </h3>
        {editMode && isAdmin ? (
          <textarea
            value={editedMedia.description || ''}
            onChange={(e) => handleInputChange('description', e.target.value)}
            className="info-textarea"
          />
        ) : (
          <p className="info-text">{mediaItem.description || 'No description available.'}</p>
        )}
      </div>

      {/* Additional Information in two columns */}
      {(type.includes('anime') || type.includes('steamgame') || type.includes('physicalgame') || type.includes('manga') || type.includes('book') || type.includes('screenmedia') || type.includes('music')) && (
        <div className="info-section">
          <h3>
            <img src="/static/icons/circle-info-solid.svg" alt="Additional Info" className="section-icon icon" />
            Additional Information
          </h3>
          <div className="info-details">
            {type.includes('music') && mediaItem.album && (
              <div className="info-item">
                <span className="info-label">Album:</span>
                <span className="info-value">{mediaItem.album}</span>
              </div>
            )}
            {(type.includes('anime') || type.includes('steamgame') || type.includes('physicalgame') || type.includes('manga')) && mediaItem.publisher && (
              <div className="info-item">
                <span className="info-label">Publisher:</span>
                <span className="info-value">{renderSearchableValue(mediaItem.publisher)}</span>
              </div>
            )}
            {type.includes('screenmedia') && mediaItem.creators && (
              <div className="info-item">
                <span className="info-label">Creators:</span>
                <span className="info-value">{mediaItem.creators}</span>
              </div>
            )}
            {type.includes('music') && mediaItem.artist && (
              <div className="info-item">
                <span className="info-label">Artist:</span>
                <span className="info-value">{mediaItem.artist}</span>
              </div>
            )}
            {type.includes('music') && mediaItem.label && (
              <div className="info-item">
                <span className="info-label">Label:</span>
                <span className="info-value">{mediaItem.label}</span>
              </div>
            )}
            {type.includes('music') && mediaItem.year && (
              <div className="info-item">
                <span className="info-label">Year:</span>
                <span className="info-value">{mediaItem.year}</span>
              </div>
            )}
            {type.includes('screenmedia') && mediaItem.release_date && (
              <div className="info-item">
                <span className="info-label">Release Date:</span>
                <span className="info-value">{mediaItem.release_date}</span>
              </div>
            )}
            {type.includes('screenmedia') && mediaItem.air_date && (
              <div className="info-item">
                <span className="info-label">Air Date:</span>
                <span className="info-value">{mediaItem.air_date}</span>
              </div>
            )}
            {(type.includes('steamgame') || type.includes('physicalgame') || type.includes('manga')) && mediaItem.developer && (
              <div className="info-item">
                <span className="info-label">Developer:</span>
                <span className="info-value">{renderSearchableValue(mediaItem.developer)}</span>
              </div>
            )}
            {type.includes('anime') && mediaItem.studios && (
              <div className="info-item">
                <span className="info-label">Studios:</span>
                <span className="info-value">{renderSearchableValue(mediaItem.studios)}</span>
              </div>
            )}
            {type.includes('anime') && mediaItem.airing_status && (
              <div className="info-item">
                <span className="info-label">Status:</span>
                <span className="info-value">{mediaItem.airing_status}</span>
              </div>
            )}
            {type.includes('anime') && mediaItem.media_type && (
              <div className="info-item">
                <span className="info-label">Type:</span>
                <span className="info-value">{mediaItem.media_type}</span>
              </div>
            )}
            {type.includes('manga') && mediaItem.publish_status && (
              <div className="info-item">
                <span className="info-label">Status:</span>
                <span className="info-value">{mediaItem.publish_status}</span>
              </div>
            )}
            {(type.includes('anime') || type.includes('manga') || type.includes('screenmedia')) && mediaItem.rating && (
              <div className="info-item">
                <span className="info-label">Rating:</span>
                <span className="info-value">{mediaItem.rating}</span>
              </div>
            )}
            {type.includes('screenmedia') && mediaItem.tmdb_score && (
              <div className="info-item">
                <span className="info-label">TMDB score:</span>
                <span className="info-value">{mediaItem.tmdb_score.toFixed(1)}</span>
              </div>
            )}
            {type.includes('screenmedia') && mediaItem.popularity && (
              <div className="info-item">
                <span className="info-label">TMDB popularity:</span>
                <span className="info-value">{mediaItem.popularity.toFixed(1)}</span>
              </div>
            )}
            {(type.includes('anime') || type.includes('manga')) && mediaItem.mal_score && (
              <div className="info-item">
                <span className="info-label">MAL score:</span>
                <span className="info-value">{mediaItem.mal_score}</span>
              </div>
            )}
            {(type.includes('anime') || type.includes('manga')) && mediaItem.rank && (
              <div className="info-item">
                <span className="info-label">MAL rank:</span>
                <span className="info-value">{mediaItem.rank}</span>
              </div>
            )}
            {(type.includes('manga')) && mediaItem.author && (
              <div className="info-item">
                <span className="info-label">Author:</span>
                <span className="info-value">{renderSearchableValue(mediaItem.author)}</span>
              </div>
            )}
            {type.includes('book') && mediaItem.authors && (
              <div className="info-item">
                <span className="info-label">Authors:</span>
                <span className="info-value">{renderSearchableValue(mediaItem.authors)}</span>
              </div>
            )}
            {type.includes('book') && mediaItem.first_publish_year && (
              <div className="info-item">
                <span className="info-label">First Published:</span>
                <span className="info-value">{mediaItem.first_publish_year}</span>
              </div>
            )}
            {type.includes('book') && mediaItem.page_count && (
              <div className="info-item">
                <span className="info-label">Page Count:</span>
                <span className="info-value">{mediaItem.page_count}</span>
              </div>
            )}
            {type.includes('book') && mediaItem.ol_score && (
              <div className="info-item">
                <span className="info-label">Open Library Score:</span>
                <span className="info-value">{mediaItem.ol_score.toFixed(1)}</span>
              </div>
            )}
            {type.includes('book') && mediaItem.isbn && (
              <div className="info-item">
                <span className="info-label">ISBN:</span>
                <span className="info-value">{mediaItem.isbn}</span>
              </div>
            )}
            {type.includes('screenmedia') && mediaItem.cast && (
              <div className="info-item">
                <span className="info-label">Cast:</span>
                <span className="info-value">{renderSearchableValue(mediaItem.cast)}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Playtime for Steam games with multiple platforms */}
      {type.includes('steamgame') && hasMultiplePlatforms && totalPlaytime > 0 && (
        <div className="info-section">
          <h3>
            <img src="/static/icons/clock-solid.svg" alt="Playtime" className="section-icon icon" />
            Playtime
          </h3>
          <div className="info-details single-column playtime-info">
            <div className="info-item">
              <span className="info-label">Total:</span>
              <span className="info-value">{(totalPlaytime / 60).toFixed(1)}h</span>
            </div>
            {mediaItem.playtime_windows && (
              <div className="info-item">
                <span className="info-label">Windows:</span>
                <span className="info-value">{(mediaItem.playtime_windows / 60).toFixed(1)}h</span>
                <div className="progress-bar">
                  <div
                    className="progress"
                    style={{
                      width: `${(mediaItem.playtime_windows / totalPlaytime) * 100}%`,
                    }}
                  ></div>
                </div>
              </div>
            )}
            {mediaItem.playtime_deck && (
              <div className="info-item">
                <span className="info-label">Steam Deck:</span>
                <span className="info-value">{(mediaItem.playtime_deck / 60).toFixed(1)}h</span>
                <div className="progress-bar">
                  <div
                    className="progress"
                    style={{
                      width: `${(mediaItem.playtime_deck / totalPlaytime) * 100}%`,
                    }}
                  ></div>
                </div>
              </div>
            )}
            {mediaItem.playtime_linux && (
              <div className="info-item">
                <span className="info-label playtime-info">Linux:</span>
                <span className="info-value playtime-info">{(mediaItem.playtime_linux / 60).toFixed(1)}h</span>
                <div className="progress-bar playtime-info">
                  <div
                    className="progress"
                    style={{
                      width: `${(mediaItem.playtime_linux / totalPlaytime) * 100}%`,
                    }}
                  ></div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Genres as tags */}
      {(mediaItem.genre || (editMode && isAdmin)) && (
        <div className="info-section full-width">
          <h3>
            <img src="/static/icons/tags-solid.svg" alt="Genres" className="section-icon icon" />
            Genres
          </h3>
          <div className="info-details">
            <div className="info-item full-width">
              {editMode && isAdmin ? (
                <input
                  value={editedMedia.genre || ''}
                  onChange={(e) => handleInputChange('genre', e.target.value)}
                  className="info-input"
                />
              ) : (
                <span className="tags">
                  {mediaItem.genre?.split(', ').map((tag, index) => (
                    <Link key={index} to={genreTagListLink(mediaListType, tag)} className="tag">{tag}</Link>
                  )) || 'None'}
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Tags as tags */}
      {(mediaItem.tags || (editMode && isAdmin)) && (
        <div className="info-section full-width">
          <h3>
            <img src="/static/icons/tags-solid.svg" alt="Tags" className="section-icon icon" />
            Tags and Themes
          </h3>
          <div className="info-details">
            <div className="info-item full-width">
              {editMode && isAdmin ? (
                <input
                  value={editedMedia.tags || ''}
                  onChange={(e) => handleInputChange('genre', e.target.value)}
                  className="info-input"
                />
              ) : (
                <span className="tags">
                  {mediaItem.tags?.split(', ').map((tag, index) => (
                    <Link key={index} to={genreTagListLink(mediaListType, tag)} className="tag">{tag}</Link>
                  )) || 'None'}
                </span>
              )}
            </div>
          </div>
        </div>
      )}


      {/* Meta Tags as tags */}
      {(type.includes('steamgame') || mediaItem.meta_tags || (editMode && isAdmin)) && (
        <div className="info-section full-width">
          <h3>
            <img src="/static/icons/tags-solid.svg" alt="Meta Tags" className="section-icon icon" />
            Meta Tags
          </h3>
          <div className="info-details">
            <div className="info-item full-width">
              {editMode && isAdmin ? (
                <input
                  value={editedMedia.meta_tags || ''}
                  onChange={(e) => handleInputChange('genre', e.target.value)}
                  className="info-input"
                />
              ) : (
                <span className="tags">
                  {mediaItem.meta_tags?.split(', ').map((tag, index) => (
                    <Link key={index} to={genreTagListLink(mediaListType, tag)} className="tag">{tag}</Link>
                  )) || 'None'}
                </span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MediaInfo;
